import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from '../context/WebSocketContext';
import { useSystemEvent } from '../context/SystemEventContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

// Maximum number of data points to keep in history
const MAX_HISTORY_POINTS = 60;

// Debug mode flag - set to false to disable console logs
const DEBUG_MODE = false;

// Conditional logging function
const debugLog = (message) => {
  if (DEBUG_MODE) {
    console.log(message);
  }
};

// Utility function to format bytes to human-readable format
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Utility function to get CSS variable value
const getCssVariableValue = (variableName) => {
  if (typeof window !== 'undefined') {
    try {
      const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
      if (!value) {
        console.warn(`SystemEventVisualizer: CSS variable ${variableName} not found or empty. Using fallback.`);
        // Fallback colors if CSS variables are not found
        const fallbacks = {
          '--color-metric-low-usage': 'rgba(75, 192, 192, 0.8)',
          '--color-metric-low-usage-bg': 'rgba(75, 192, 192, 0.2)',
          '--color-metric-medium-usage': 'rgba(255, 205, 86, 0.8)',
          '--color-metric-medium-usage-bg': 'rgba(255, 205, 86, 0.2)',
          '--color-metric-high-usage': 'rgba(255, 99, 132, 0.8)',
          '--color-metric-high-usage-bg': 'rgba(255, 99, 132, 0.2)',
          '--color-metric-info': 'rgba(54, 162, 235, 0.8)',
          '--color-metric-info-bg': 'rgba(54, 162, 235, 0.2)',
        };
        return fallbacks[variableName] || ''; // Return specific fallback or empty string
      }
      return value;
    } catch (error) {
      console.error(`SystemEventVisualizer: Error getting CSS variable ${variableName}:`, error);
      return ''; // Fallback in case of error
    }
  }
  return ''; // Fallback for non-browser environments (e.g., SSR)
};

// Utility function to get color based on usage percentage
const getUsageColor = (percent) => {
  if (percent < 60) return getCssVariableValue('--color-metric-low-usage');
  if (percent < 80) return getCssVariableValue('--color-metric-medium-usage');
  return getCssVariableValue('--color-metric-high-usage');
};

// Utility function to get background color with transparency
const getBackgroundColor = (percent) => { // Parameter changed from color to percent
  if (percent < 60) return getCssVariableValue('--color-metric-low-usage-bg');
  if (percent < 80) return getCssVariableValue('--color-metric-medium-usage-bg');
  return getCssVariableValue('--color-metric-high-usage-bg');
};

const SystemEventVisualizer = ({ serverId }) => {
  const { getServerMessageByType, subscribe } = useWebSocket();
  const { updateServerMetrics, getServerMetrics } = useSystemEvent();
  
  // Subscribe to system events for this server
  useEffect(() => {
    if (serverId) {
      // Subscribe to system events for this server
      const topic = `persistent://public/default/${serverId}_systemevent_events`;
      subscribe(topic);
      debugLog(`SystemEventVisualizer: Subscribed to ${topic}`);
    }
  }, [serverId, subscribe]);
  
  // Local state for rendering - will be updated from the persistent store
  const [cpuHistory, setCpuHistory] = useState([]);
  const [memoryHistory, setMemoryHistory] = useState([]);
  const [diskHistory, setDiskHistory] = useState([]);
  const [networkHistory, setNetworkHistory] = useState([]);
  const [timeLabels, setTimeLabels] = useState([]);
  const [currentMetrics, setCurrentMetrics] = useState({
    cpu: { percent: 0, count: 0, perCpu: [] },
    memory: { total: 0, available: 0, used: 0, percent: 0 },
    disk: { total: 0, used: 0, free: 0, percent: 0 },
    network: { bytes_sent: 0, bytes_recv: 0, packets_sent: 0, packets_recv: 0 }
  });
  
  // Reference to track previous message timestamp to avoid duplicates
  const lastProcessedTimestamp = useRef(null);
  
  // Extract system metrics from the event data
  const extractSystemData = useCallback((eventData) => {
    if (!eventData) return null;
    
    // Handle different formats of SystemEvent data
    if (eventData.SystemEvent && typeof eventData.SystemEvent === 'object') {
      return eventData.SystemEvent;
    } else if (eventData.system && typeof eventData.system === 'object') {
      return eventData.system;
    } else if (eventData.data && typeof eventData.data === 'object') {
      return eventData.data;
    } else {
      // Try to use the data directly if it has system metrics keys
      const hasSystemKeys = ['memory', 'cpu', 'disk', 'network'].some(key => 
        key in eventData && typeof eventData[key] === 'object'
      );
      
      if (hasSystemKeys) {
        return eventData;
      }
    }
    return null;
  }, []);

  // Process system event data and update metrics
  const processSystemEvent = useCallback((eventData) => {
    if (!eventData || !serverId) return;
    
    // Extract event time for deduplication
    const eventTime = eventData.event_time || new Date().toISOString();
    
    // Skip if we've already processed this timestamp
    if (lastProcessedTimestamp.current === eventTime) return;
    lastProcessedTimestamp.current = eventTime;
    
    // Update the persistent store - this will handle all the data processing
    // and will trigger a store update event that our component will receive
    updateServerMetrics(serverId, eventData, eventTime);
    
    // No need to manually update our state here because we'll receive the store update event
  }, [serverId, updateServerMetrics]);

  // Function to sync component state with the latest data from the store
  const syncWithLatestData = useCallback(() => {
    if (!serverId) return;
    
    const cachedMetrics = getServerMetrics(serverId);
    if (cachedMetrics) {
      // Update all our local state with the latest data from the store
      setCurrentMetrics(cachedMetrics.currentMetrics);
      setCpuHistory(cachedMetrics.cpuHistory);
      setMemoryHistory(cachedMetrics.memoryHistory);
      setDiskHistory(cachedMetrics.diskHistory);
      setNetworkHistory(cachedMetrics.networkHistory);
      setTimeLabels(cachedMetrics.timeLabels);
      debugLog(`SystemEventVisualizer: Synced with latest data for ${serverId}`);
    } else {
      // If no cached metrics, try to get initial data from WebSocket context
      const initialSystemEvent = getServerMessageByType(serverId, 'systemevent');
      if (initialSystemEvent && initialSystemEvent.data) {
        processSystemEvent(initialSystemEvent.data);
        debugLog(`SystemEventVisualizer: Processed initial data for ${serverId}`);
      }
    }
  }, [serverId, getServerMetrics, getServerMessageByType, processSystemEvent]);
  
  // Initial data load and setup event listeners for real-time updates
  useEffect(() => {
    if (!serverId) return;
    
    const normalizedServerId = serverId.toLowerCase();
    debugLog(`SystemEventVisualizer: Setting up for server ${serverId}`);
    
    // Load initial data
    syncWithLatestData();
    
    // Handler for direct WebSocket events - process them immediately
    const handleSystemEvent = (event) => {
      // Check if this event is for our server
      if (event.detail && event.detail.serverId === serverId) {
        processSystemEvent(event.detail.data);
      }
    };
    
    // Handler for store update events - this is the primary update mechanism
    const handleStoreUpdate = (event) => {
      // Check if this event is for our server
      if (event.detail && event.detail.serverId === normalizedServerId) {
        // Update component state directly from the event data
        const metrics = event.detail.metrics;
        if (metrics) {
          const isVisible = document.visibilityState === 'visible';
          const logPrefix = isVisible ? 'Visible update' : 'Background update';
          
          // Update our local state with the latest data
          setCurrentMetrics(metrics.currentMetrics);
          setCpuHistory(metrics.cpuHistory);
          setMemoryHistory(metrics.memoryHistory);
          setDiskHistory(metrics.diskHistory);
          setNetworkHistory(metrics.networkHistory);
          setTimeLabels(metrics.timeLabels);
          debugLog(`SystemEventVisualizer: ${logPrefix} from store event for ${serverId}`);
        }
      }
    };
    
    // Add event listeners for real-time updates
    window.addEventListener('systemevent-update', handleSystemEvent);
    window.addEventListener('systemevent-store-updated', handleStoreUpdate);
    
    // Cleanup
    return () => {
      window.removeEventListener('systemevent-update', handleSystemEvent);
      window.removeEventListener('systemevent-store-updated', handleStoreUpdate);
    };
  }, [serverId, processSystemEvent, syncWithLatestData]);
  
  // Set up visibility change detection to sync when tab becomes visible again
  useEffect(() => {
    if (!serverId) return;
    
    // Function to handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        debugLog(`Tab became visible, syncing with latest data for ${serverId}`);
        // Immediate sync when tab becomes visible to show the latest data
        syncWithLatestData();
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [serverId, syncWithLatestData]);
  
  // Debug log when component renders
  useEffect(() => {
    debugLog(`SystemEventVisualizer rendering for server ${serverId}`);
    return () => {
      debugLog(`SystemEventVisualizer unmounting for server ${serverId}`);
    };
  }, [serverId]);
  
  // Chart options and data
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: value => `${value}%`
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: context => `${context.parsed.y.toFixed(1)}%`
        }
      }
    },
    animation: {
      duration: 500
    }
  };
  
  const networkChartOptions = {
    ...lineChartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: value => `${value.toFixed(1)} MB/s`
        }
      }
    },
    plugins: {
      ...lineChartOptions.plugins,
      tooltip: {
        callbacks: {
          label: context => `${context.parsed.y.toFixed(2)} MB/s`
        }
      }
    }
  };
  
  const cpuPerCoreOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: value => `${value}%`
        }
      }
    },
    plugins: {
      legend: {
        display: false
      }
    },
    animation: {
      duration: 500
    }
  };
  
  // Prepare chart data
  const cpuData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'CPU Usage',
        data: cpuHistory,
        borderColor: getUsageColor(currentMetrics.cpu.percent),
        backgroundColor: getBackgroundColor(currentMetrics.cpu.percent),
        fill: true,
        tension: 0.4
      }
    ]
  };
  
  const memoryData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Memory Usage',
        data: memoryHistory,
        borderColor: getUsageColor(currentMetrics.memory.percent),
        backgroundColor: getBackgroundColor(currentMetrics.memory.percent),
        fill: true,
        tension: 0.4
      }
    ]
  };
  
  const diskData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Disk Usage',
        data: diskHistory,
        borderColor: getUsageColor(currentMetrics.disk.percent),
        backgroundColor: getBackgroundColor(currentMetrics.disk.percent),
        fill: true,
        tension: 0.4
      }
    ]
  };
  
  const networkData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Network Throughput',
        data: networkHistory,
        borderColor: getCssVariableValue('--color-metric-info'),
        backgroundColor: getCssVariableValue('--color-metric-info-bg'),
        fill: true,
        tension: 0.4
      }
    ]
  };
  
  // Prepare CPU per core data
  const cpuPerCoreData = {
    labels: currentMetrics.cpu.perCpu.map((_, index) => `Core ${index + 1}`),
    datasets: [
      {
        label: 'CPU Usage Per Core',
        data: currentMetrics.cpu.perCpu,
        backgroundColor: currentMetrics.cpu.perCpu.map(usage => getUsageColor(usage)),
        borderWidth: 1
      }
    ]
  };
  
  // Prepare memory usage doughnut chart data
  const memoryDoughnutData = {
    labels: ['Used', 'Available'],
    datasets: [
      {
        data: [
          currentMetrics.memory.used,
          currentMetrics.memory.available
        ],
        backgroundColor: [
          getUsageColor(currentMetrics.memory.percent),
          getCssVariableValue('--color-metric-info')
        ],
        borderWidth: 1
      }
    ]
  };
  
  // Prepare disk usage doughnut chart data
  const diskDoughnutData = {
    labels: ['Used', 'Free'],
    datasets: [
      {
        data: [
          currentMetrics.disk.used,
          currentMetrics.disk.free
        ],
        backgroundColor: [
          getUsageColor(currentMetrics.disk.percent),
          getCssVariableValue('--color-metric-info')
        ],
        borderWidth: 1
      }
    ]
  };
  
  return (
    <div className="system-event-visualizer">
      <div className="metrics-grid">
        {/* CPU Usage Section */}
        <div className="metric-card">
          <div className="metric-header">
            <h3>CPU Usage</h3>
            <div className="metric-value" style={{ color: getUsageColor(currentMetrics.cpu.percent) }}>
              {currentMetrics.cpu.percent.toFixed(1)}%
            </div>
          </div>
          <div className="chart-container">
            <Line options={lineChartOptions} data={cpuData} />
          </div>
          <div className="metric-details">
            <div>CPU Cores: {currentMetrics.cpu.count}</div>
          </div>
        </div>
        
        {/* Memory Usage Section */}
        <div className="metric-card">
          <div className="metric-header">
            <h3>Memory Usage</h3>
            <div className="metric-value" style={{ color: getUsageColor(currentMetrics.memory.percent) }}>
              {currentMetrics.memory.percent.toFixed(1)}%
            </div>
          </div>
          <div className="chart-container">
            <Line options={lineChartOptions} data={memoryData} />
          </div>
          <div className="metric-details">
            <div>Total: {formatBytes(currentMetrics.memory.total)}</div>
            <div>Used: {formatBytes(currentMetrics.memory.used)}</div>
            <div>Available: {formatBytes(currentMetrics.memory.available)}</div>
          </div>
        </div>
        
        {/* Disk Usage Section */}
        <div className="metric-card">
          <div className="metric-header">
            <h3>Disk Usage</h3>
            <div className="metric-value" style={{ color: getUsageColor(currentMetrics.disk.percent) }}>
              {currentMetrics.disk.percent.toFixed(1)}%
            </div>
          </div>
          <div className="chart-container">
            <Line options={lineChartOptions} data={diskData} />
          </div>
          <div className="metric-details">
            <div>Total: {formatBytes(currentMetrics.disk.total)}</div>
            <div>Used: {formatBytes(currentMetrics.disk.used)}</div>
            <div>Free: {formatBytes(currentMetrics.disk.free)}</div>
          </div>
        </div>
        
        {/* Network Usage Section */}
        <div className="metric-card">
          <div className="metric-header">
            <h3>Network Throughput</h3>
            <div className="metric-value">
              {formatBytes(currentMetrics.network.total_bytes_rate || 0)}/s
            </div>
          </div>
          <div className="chart-container">
            <Line options={networkChartOptions} data={networkData} />
          </div>
          <div className="metric-details">
            <div>Sent: {formatBytes(currentMetrics.network.bytes_sent_rate || 0)}/s</div>
            <div>Received: {formatBytes(currentMetrics.network.bytes_recv_rate || 0)}/s</div>
            <div>Total Sent: {formatBytes(currentMetrics.network.bytes_sent)}</div>
            <div>Total Received: {formatBytes(currentMetrics.network.bytes_recv)}</div>
            <div>Packets Rate: {Math.round(currentMetrics.network.total_packets_rate || 0)}/s</div>
          </div>
        </div>
        
        {/* CPU Per Core Section */}
        {currentMetrics.cpu.perCpu.length > 0 && (
          <div className="metric-card wide">
            <div className="metric-header">
              <h3>CPU Usage Per Core</h3>
            </div>
            <div className="chart-container">
              <Bar options={cpuPerCoreOptions} data={cpuPerCoreData} />
            </div>
          </div>
        )}
        
        {/* Memory and Disk Distribution */}
        <div className="metric-card">
          <div className="metric-header">
            <h3>Memory Distribution</h3>
          </div>
          <div className="chart-container doughnut-container">
            <Doughnut 
              data={memoryDoughnutData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        return `${label}: ${formatBytes(value)}`;
                      }
                    }
                  }
                }
              }} 
            />
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-header">
            <h3>Disk Distribution</h3>
          </div>
          <div className="chart-container doughnut-container">
            <Doughnut 
              data={diskDoughnutData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        return `${label}: ${formatBytes(value)}`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemEventVisualizer;
